import React, { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { UserCircleIcon } from "lucide-react";
import ReactWebcam from "react-webcam";

const ProfilePage = () => {
  const { user } = useContext(AuthContext);
  const [userData, setUserData] = useState({});
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    mobileNumber: "",
    birthdayDate: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [profileImage, setProfileImage] = useState(null);  
  const [useWebcam, setUseWebcam] = useState(false);
const webcamRef = React.useRef(null);

  const isBusinessAccount = [
    "SUPER_SUPER_ADMIN",
    "SUPER_ADMIN",
    "COMPANY",
    "RESELLER",
  ].includes(user?.accountType);
  const isEmployeeAccount = [
    "VICE_PRESIDENT",
    "MANAGER",
    "TEAM_LEAD",
    "HR",
    "EXECUTIVE",
  ].includes(user?.accountType);

  const getProfileImageUrl = useCallback(
    async (filename) => {
      if (!filename) {
        setProfileImage(null); // Set to null to use default icon
        return;
      }
  
      const endpoint = isBusinessAccount
        ? `${API_URL}/api/user/profileImage/${filename}`
        : `${API_URL}/api/companyuser/profileImage/${filename}`;
  
      try {
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        });
  
        const imageUrl = URL.createObjectURL(response.data);
        setProfileImage(imageUrl);
        localStorage.setItem('profileImage', imageUrl);
      } catch (error) {
        console.error("Error loading profile image:", error);
        setProfileImage(null);
      }
    },
    [isBusinessAccount]
  );

  const fetchUserData = useCallback(async () => {
    if (!user) return;

    const { id: userId } = user;
    const endpoint = isBusinessAccount
      ? `${API_URL}/api/user/details/${userId}`
      : `${API_URL}/api/companyuser/details/${userId}`;

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = response.data;
      setUserData(data);
      setFormData({
        fname: data.fname || "",
        lname: data.lname || "",
        mobileNumber: data.mobileNumber || "",
        birthdayDate: data.birthdayDate || "",
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [user, isBusinessAccount]);

  useEffect(() => {
    if (userData.profileImage) {
      getProfileImageUrl(userData.profileImage);
    }
  }, [userData.profileImage, getProfileImageUrl]);

  useEffect(() => {
    fetchUserData();
    return () => {
      if (previewPhoto?.startsWith("blob:")) {
        URL.revokeObjectURL(previewPhoto);
      }
    };
  }, [fetchUserData, previewPhoto]);

  const handleCapture = async () => {
  const imageSrc = webcamRef.current.getScreenshot();

  if (!imageSrc) return;

  const blob = await (await fetch(imageSrc)).blob();
  const file = new File([blob], "captured-image.jpg", { type: "image/jpeg" });

  const imageForm = new FormData();
  imageForm.append("profileImage", file);

  try {
    const endpoint = isBusinessAccount
      ? `${API_URL}/api/user/updateProfileImage/${user.id}`
      : `${API_URL}/api/companyuser/updateProfileImage/${user.id}/changedBy/${user.id}`;

    await axios.put(endpoint, imageForm, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    toast.success("✅ Profile image captured and uploaded");
    setUseWebcam(false);
    fetchUserData();
  } catch (error) {
    console.error("Webcam image upload failed:", error);
    toast.error("❌ Failed to upload webcam image");
  }
};

  const handleImageChange = async (e) => {
    
    const file = e.target.files[0];
    if (!file) return;

    if (previewPhoto?.startsWith("blob:")) {
      URL.revokeObjectURL(previewPhoto);
    }

    const previewUrl = URL.createObjectURL(file);
    setPreviewPhoto(previewUrl);

    const imageForm = new FormData();
    imageForm.append("profileImage", file);

    try {
      const endpoint = isBusinessAccount
        ? `${API_URL}/api/user/updateProfileImage/${user.id}`
        : `${API_URL}/api/companyuser/updateProfileImage/${user.id}/changedBy/${user.id}`;

      await axios.put(endpoint, imageForm, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("✅ Profile image updated successfully");
      fetchUserData();
    } catch (error) {
      console.error("Image upload failed:", error);
      toast.error("❌ Failed to upload image. Please try again.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const endpoint = isBusinessAccount
        ? `${API_URL}/api/user/updateProfile/${user.id}`
        : `${API_URL}/api/companyuser/updateProfile/${user.id}/changedBy/${user.id}`;

      await axios.put(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Profile updated successfully");
      fetchUserData();
    } catch (error) {
      console.error("Profile update failed:", error);
      toast.error("Failed to update profile");
    }
    setIsSaving(false);
  };

  const handlePasswordChange = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error("Both fields are required");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      const endpoint = isBusinessAccount
      ? `${API_URL}/api/user/set-password/${user.id}/changedBy/${user.id}`
      : `${API_URL}/api/companyuser/set-password/${user.id}/changedBy/${user.id}`
      await axios.post(
        endpoint,
        {
          newPassword,
          confirmPassword: confirmNewPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("✅ Password updated successfully");
      setIsPasswordModalOpen(false);
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      console.error("Password update failed:", error);
      toast.error("❌ Failed to update password");
    }
  };

  return (
    <div className="w-full mx-auto mt-8 p-2 bg-gray-100 dark:bg-gray-900 rounded-xl shadow-xl transition-all">
      <h1 className="text-3xl font-bold mb-10 text-center text-gray-900 dark:text-white">
        Profile Information
      </h1>

      <div className="flex flex-col md:flex-row items-start md:items-center gap-10">
      <div className="relative">
  {profileImage ? (
    <img
      src={profileImage}
      onError={() => setProfileImage(null)}
      alt="Profile"
      className="w-48 h-48 rounded-2xl object-cover border-4 border-gray-300 dark:border-gray-700 shadow-lg"
    />
  ) : (
    <div className="w-48 h-48 rounded-2xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-4 border-gray-300 dark:border-gray-700 shadow-lg">
      <UserCircleIcon className="h-32 w-32 text-gray-500 dark:text-gray-400" />
    </div>
  )}

  <div className="absolute bottom-0 right-0 flex gap-2">
    {/* Upload Button */}
    <label className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3 py-1 rounded-full cursor-pointer shadow-md">
      Upload
      <input
        type="file"
        onChange={handleImageChange}
        accept="image/*"
        className="hidden"
      />
    </label>

    {/* Webcam Button */}
    <button
      onClick={() => setUseWebcam((prev) => !prev)}
      className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded-full shadow-md"
    >
      {useWebcam ? "Cancel" : "Use Webcam"}
    </button>
  </div>

  {/* Webcam View */}
  {useWebcam && (
    <div className="absolute top-full mt-4 z-50 bg-white dark:bg-gray-900 p-4 rounded-xl shadow-xl">
      <ReactWebcam
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        videoConstraints={{ facingMode: "user" }}
        className="rounded-xl w-64 h-48 object-cover"
      />
      <div className="flex justify-end mt-2">
        <button
          onClick={handleCapture}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-md"
        >
          Capture
        </button>
      </div>
    </div>
  )}
</div>

        <div className="flex-1 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                First Name
              </label>
              <input
                type="text"
                name="fname"
                value={formData.fname}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Last Name
              </label>
              <input
                type="text"
                name="lname"
                value={formData.lname}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Email
              </label>
              <input
                type="email"
                value={userData.email || ""}
                disabled
                className="w-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-700 p-2 rounded-md mt-1 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Phone
              </label>
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white"
              />
            </div>

            {isEmployeeAccount && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Company Name
                </label>
                <input
                  type="text"
                  value={userData.companyName || ""}
                  disabled
                  className="w-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-700 p-2 rounded-md mt-1 cursor-not-allowed"
                />
              </div>
            )}

            {isBusinessAccount && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                  GST No.
                </label>
                <input
                  type="text"
                  value={userData.gstNo || ""}
                  readOnly
                  className="w-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700 p-2 rounded-md mt-1"
                />
              </div>
            )}

            <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Account Type
                </label>
                <input
                  type="text"
                  value={userData.accountType || ""}
                  readOnly
                  className="w-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700 p-2 rounded-md mt-1"
                />
              </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                 DOB
              </label>
              <input
                type="date"
                name="birthdayDate"
                value={formData.birthdayDate}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row justify-end gap-4">
            <button
              onClick={() => setIsPasswordModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium shadow-lg transition-all"
            >
              Change Password
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium shadow-lg transition-all disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
      {isPasswordModalOpen && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center">
    <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl w-full max-w-md shadow-xl relative">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white text-center">Change Password</h2>
      
      <div className="mb-4 relative">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">New Password</label>
        <div className="relative">
          <input
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full p-2 pr-10 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white"
          />
          <span
            className="absolute inset-y-0 right-2 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
            onClick={() => setShowNewPassword((prev) => !prev)}
          >
            {showNewPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
      </div>

      <div className="mb-4 relative">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">Confirm Password</label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            className={`w-full p-2 pr-10 border ${
              newPassword && confirmNewPassword && newPassword !== confirmNewPassword
                ? "border-red-500"
                : "border-gray-300 dark:border-gray-700"
            } bg-white dark:bg-gray-800 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white`}
          />
          <span
            className="absolute inset-y-0 right-2 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
            onClick={() => setShowConfirmPassword((prev) => !prev)}
          >
            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
        {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
          <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          className="px-4 py-2 rounded-md bg-gray-400 hover:bg-gray-500 text-white"
          onClick={() => {
            setIsPasswordModalOpen(false);
            setNewPassword("");
            setConfirmNewPassword("");
          }}
        >
          Cancel
        </button>
        <button
          onClick={handlePasswordChange}
          className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          Save
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default ProfilePage;
